/**
 * REL-06 — which incoming https links the native app answers, and where each one goes.
 *
 * REL-02 (PR #671) claimed four paths on www.afriqahome.com for the app, in
 * AndroidManifest.xml and public/.well-known/apple-app-site-association. That
 * claim only makes the OS *open* the app: Capacitor delivers the URL as an
 * `appUrlOpen` event (Android `AppPlugin.handleOnNewIntent`, iOS
 * `CAPApplicationDelegateProxy` → `capacitorOpenUniversalLink`) and never
 * navigates the webview itself. Without a handler the claim is a regression —
 * a link that used to open the browser on the right page now opens the app on
 * whatever screen it last showed.
 *
 * This module is the decision half: given a raw incoming URL, the SPA path to
 * navigate to, or null to ignore it. It is deliberately free of React and of
 * @capacitor/*, so it is directly testable under `npx tsx` and so the web
 * bundle never pulls a plugin in on its account. The wiring half —
 * `appUrlOpen`, `getLaunchUrl`, and the router — is src/lib/nativeDeepLink.ts.
 *
 * THE ALLOWLIST IS A MIRROR, NOT A SOURCE
 *
 * Every entry below must match the two REL-02 artifacts exactly. Claiming a
 * path the app cannot route strands the user; routing a path the OS never
 * claims is dead code. nativeDeepLinkRouting.test.ts parses both artifacts and
 * fails if the three lists ever diverge, so this comment cannot go stale
 * quietly. Anything added here must be added there — and vice versa.
 */

/** The single host registered in REL-02. Apex is out of scope (REL-05). */
export const DEEP_LINK_HOST = 'www.afriqahome.com';

/**
 * Exact paths — `android:path` on Android, a `components` entry with no
 * wildcard on iOS.
 *
 * `/auth` is exact rather than a prefix on all three surfaces so that
 * `/auth/callback` is NOT claimed: that path belongs to the custom-scheme OAuth
 * round-trip (BUG-034) and must never be answered here.
 */
const EXACT_PATHS: readonly string[] = ['/auth', '/syndication'];

/**
 * Prefix paths — `android:pathPrefix` on Android, a `/*` component on iOS.
 *
 * Mirrored literally, trailing slash included. A bare `/property/` therefore
 * resolves and lands on REL-03's in-shell not-found view rather than being
 * ignored, which is what Android's pathPrefix does too: the OS opens the app
 * for that URL either way, so answering it honestly beats leaving the user on
 * an unrelated screen with no explanation.
 */
const PREFIX_PATHS: readonly string[] = ['/property/', '/syndicated/'];

/**
 * The SPA path an incoming URL should navigate to, or null to leave it alone.
 *
 * Returns `pathname + search + hash` so the query survives: `/auth?ref=CODE` is
 * the team-invite accept path (lib/teamInvite.ts) and the code is the entire
 * point of the link.
 *
 * MUTUAL EXCLUSION WITH THE OAUTH LISTENER. Both listeners see every
 * `appUrlOpen` event, so exactly one of them must claim any given URL. This one
 * requires `https:`; `useNativeOAuthCallback` requires the URL to start with
 * `com.afriqahome.app://auth/callback`, whose protocol parses as
 * `com.afriqahome.app:`. No string can satisfy both, and no string can satisfy
 * neither-but-look-like-either. The test asserts this against the real
 * NATIVE_OAUTH_REDIRECT constant read out of nativeOAuth.ts rather than a copy.
 */
export function resolveDeepLinkPath(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  // The OAuth custom scheme is excluded here, by protocol. See above.
  if (parsed.protocol !== 'https:') return null;
  if (parsed.hostname !== DEEP_LINK_HOST) return null;

  const { pathname } = parsed;
  const claimed =
    EXACT_PATHS.includes(pathname) ||
    PREFIX_PATHS.some((prefix) => pathname.startsWith(prefix));
  if (!claimed) return null;

  return `${pathname}${parsed.search}${parsed.hash}`;
}

/** The claimed paths, in the form the mirror test compares against. */
export const DEEP_LINK_ALLOWLIST = {
  exact: EXACT_PATHS,
  prefix: PREFIX_PATHS,
} as const;
